import React, { useState, useEffect } from 'react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Calendar, Download, FileText, Building, User, Filter, CheckCircle, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { TaxReturnExportButtons } from './TaxReturnExportButtons';
import { getCsrfToken } from '../../utils/apiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

import { BACKEND_BASE } from '../../utils/apiUrl';
const API_URL = BACKEND_BASE;

const SYMBOLS = { NGN: '₦', USD: '$', GBP: '£', EUR: '€', GHS: '₵', KES: 'KSh ', ZAR: 'R', CAD: 'CA$', AUD: 'A$', INR: '₹', TZS: 'TSh ', UGX: 'USh ', RWF: 'RF ' };
const formatMoney = (amount, currency) => `${SYMBOLS[currency] || `${currency || ''} `}${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function WHTRemittanceReport() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [pendingWHT, setPendingWHT] = useState(null);
  const [whtConfig, setWhtConfig] = useState(null);
  const profile = whtConfig?.profile || {};
  const reportCurrency = reportData?.currency || whtConfig?.currency || 'USD';
  const formatCurrency = (amount, currency) => formatMoney(amount, currency || reportCurrency);
  const [splitA, splitB] = profile.split_labels || ['Companies', 'Individuals'];
  const taxName = profile.tax_name || 'Withholding Tax';
  
  // Filters
  const [startDate, setStartDate] = useState(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [entityFilter, setEntityFilter] = useState('all');
  
  // Remittance dialog
  const [showRemitDialog, setShowRemitDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [remittanceRef, setRemittanceRef] = useState('');
  const [remittanceDate, setRemittanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [remitting, setRemitting] = useState(false);

  useEffect(() => {
    fetchPendingWHT();
    fetchWHTConfig();
  }, []);

  const fetchWHTConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wht/config`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWhtConfig(data);
      }
    } catch (error) {
      console.error('Error fetching WHT config:', error);
    }
  };

  const fetchPendingWHT = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wht/pending-remittance`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingWHT(data);
      }
    } catch (error) {
      console.error('Error fetching pending WHT:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      // UNIFIED: Fetch WHT Remittance from unified endpoint (Single Source of Truth - journals_unified)
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      
      const response = await fetch(`${API_URL}/api/unified-reports/wht-remittance?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Get WHT payable transactions (corporate + individual) from the unified API response
        const corporateTxns = data.wht_payable?.corporate?.transactions || [];
        const individualTxns = data.wht_payable?.individual?.transactions || [];
        const allPayableTxns = [...corporateTxns, ...individualTxns];
        
        // Map transactions to component format
        let deductionTransactions = allPayableTxns.map((t, idx) => ({
          id: `ded-${idx}`,
          payment_date: t.date,
          bill_reference: t.reference,
          vendor_name: t.vendor || t.description?.split(' from ')?.[1]?.split(' (')?.[0] || 'Unknown',
          entity_type: t.entity_type?.toLowerCase() || (t.account_code === '2321' ? 'individual' : 'corporate'),
          category: t.description?.includes('WHT') ? 'Services' : 'Goods',
          gross_amount: (t.amount || 0) * 20, // Estimate gross from 5% WHT
          wht_rate: 5.0,
          wht_amount: t.amount || 0,
          net_payment: (t.amount || 0) * 19,
          remitted: false,
          remittance_reference: null,
          account_code: t.account_code,
          account_name: t.account_name,
          source_type: t.source_type
        }));
        
        // Apply entity filter
        let filteredTransactions = deductionTransactions;
        if (entityFilter !== 'all') {
          filteredTransactions = deductionTransactions.filter(t => t.entity_type === entityFilter);
        }
        
        // Calculate by entity type
        const corporateTransactions = deductionTransactions.filter(t => t.entity_type === 'corporate');
        const individualTransactions = deductionTransactions.filter(t => t.entity_type === 'individual');
        
        // Group by vendor
        const vendorGroups = {};
        filteredTransactions.forEach(t => {
          if (!vendorGroups[t.vendor_name]) {
            vendorGroups[t.vendor_name] = {
              vendor_name: t.vendor_name,
              entity_type: t.entity_type,
              transaction_count: 0,
              total_gross: 0,
              total_wht: 0
            };
          }
          vendorGroups[t.vendor_name].transaction_count++;
          vendorGroups[t.vendor_name].total_gross += t.gross_amount;
          vendorGroups[t.vendor_name].total_wht += t.wht_amount;
        });
        
        // Transform unified response to expected format for the component
        setReportData({
          report_period: {
            start_date: data.period?.start || startDate,
            end_date: data.period?.end || endDate
          },
          period: data.period,
          summary: {
            total_deducted: data.wht_payable?.total || data.summary?.total_withheld || 0,
            total_remitted: data.summary?.total_remitted || 0,
            outstanding: data.remittance_due || data.summary?.balance_due || 0,
            deductions_count: data.summary?.transaction_count || allPayableTxns.length,
            remittances_count: 0,
            total_transactions: filteredTransactions.length,
            total_gross_amount: filteredTransactions.reduce((sum, t) => sum + t.gross_amount, 0),
            total_wht_amount: filteredTransactions.reduce((sum, t) => sum + t.wht_amount, 0),
            corporate_wht: data.wht_payable?.corporate?.total || corporateTransactions.reduce((sum, t) => sum + t.wht_amount, 0),
            individual_wht: data.wht_payable?.individual?.total || individualTransactions.reduce((sum, t) => sum + t.wht_amount, 0)
          },
          by_entity_type: {
            corporate: {
              count: corporateTransactions.length,
              total_wht: data.wht_payable?.corporate?.total || corporateTransactions.reduce((sum, t) => sum + t.wht_amount, 0)
            },
            individual: {
              count: individualTransactions.length,
              total_wht: data.wht_payable?.individual?.total || individualTransactions.reduce((sum, t) => sum + t.wht_amount, 0)
            }
          },
          by_vendor: Object.values(vendorGroups).sort((a, b) => b.total_wht - a.total_wht),
          by_category: [], // Would need more data to group by category
          transactions: filteredTransactions,
          deductions: allPayableTxns,
          remittances: [],
          source: 'journals_unified',
          currency: data.currency
        });
      } else {
        const errorText = await response.text();
        console.error('WHT Report fetch failed:', errorText);
        toast.error('Failed to fetch report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error fetching WHT report');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRemitted = async () => {
    if (!remittanceRef.trim()) {
      toast.error('Please enter a remittance reference');
      return;
    }
    
    if (selectedTransactions.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    setRemitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wht/remittance-report/mark-remitted`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken() || '',
        },
        body: JSON.stringify({
          transaction_ids: selectedTransactions,
          remittance_reference: remittanceRef,
          remittance_date: remittanceDate
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowRemitDialog(false);
        setSelectedTransactions([]);
        setRemittanceRef('');
        fetchReport();
        fetchPendingWHT();
      } else {
        toast.error('Failed to mark as remitted');
      }
    } catch (error) {
      console.error('Error marking remitted:', error);
      toast.error('Error updating remittance status');
    } finally {
      setRemitting(false);
    }
  };

  const toggleTransaction = (id) => {
    setSelectedTransactions(prev => 
      prev.includes(id) 
        ? prev.filter(t => t !== id)
        : [...prev, id]
    );
  };

  const selectAllUnremitted = () => {
    if (!reportData?.transactions) return;
    const unremitted = reportData.transactions
      .filter(t => !t.remitted)
      .map(t => t.id);
    setSelectedTransactions(unremitted);
  };

  const exportCSV = () => {
    if (!reportData?.transactions) return;
    
    const headers = ['Payment Date', 'Bill Reference', 'Vendor', 'Entity Type', 'Category', 'Gross Amount', 'WHT Rate', 'WHT Amount', 'Net Payment', 'Remitted', 'Remittance Ref'];
    const rows = reportData.transactions.map(t => [
      t.payment_date,
      t.bill_reference,
      t.vendor_name,
      t.entity_type,
      t.category,
      t.gross_amount,
      `${t.wht_rate}%`,
      t.wht_amount,
      t.net_payment,
      t.remitted ? 'Yes' : 'No',
      t.remittance_reference || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wht_report_${startDate}_to_${endDate}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {profile.regime && profile.regime !== 'general' && (
        <Card className="border-sky-200 bg-sky-50" data-testid="wht-regime-note">
          <CardContent className="pt-4 text-sm text-sky-900">
            <p className="font-medium">{profile.regime === 'none' ? `No general withholding tax regime in ${whtConfig.country}` : `Limited withholding regime in ${whtConfig.country}`}</p>
            <p className="text-sky-800 mt-1">{profile.note}</p>
          </CardContent>
        </Card>
      )}
      {profile.regime === 'general' && profile.note && (
        <p className="text-xs text-muted-foreground" data-testid="wht-regime-note">{profile.note}</p>
      )}

      {/* Pending WHT Summary Cards */}
      {pendingWHT && pendingWHT.total_pending_wht > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700 font-medium">Pending {taxName} to remit</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {formatCurrency(pendingWHT.total_pending_wht, pendingWHT.currency)}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs text-amber-600 mt-2">Due for remittance to tax authority</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{splitA}</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(pendingWHT.by_entity_type?.corporate?.total_wht || 0, pendingWHT.currency)}
                  </p>
                </div>
                <Building className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {pendingWHT.by_entity_type?.corporate?.count || 0} transactions
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{splitB}</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(pendingWHT.by_entity_type?.individual?.total_wht || 0, pendingWHT.currency)}
                  </p>
                </div>
                <User className="w-6 h-6 text-purple-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {pendingWHT.by_entity_type?.individual?.count || 0} transactions
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {taxName} — remittance report{whtConfig?.country ? ` (${whtConfig.country})` : ''}
          </CardTitle>
          <CardDescription data-testid="wht-country-description">
            {profile.authority ? <>Amounts in {reportCurrency}. File as <strong>{profile.return_name}</strong> with {profile.authority} — due {profile.due_rule}.</> : 'Generate detailed reports for tax authority submission'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="wht-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="wht-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-40" data-testid="wht-entity-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="corporate">{splitA} only</SelectItem>
                  <SelectItem value="individual">{splitB} only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchReport} disabled={loading} data-testid="generate-wht-report-btn">
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Report: {reportData.report_period?.start_date} to {reportData.report_period?.end_date}</CardTitle>
                <CardDescription>
                  {reportData.summary?.total_transactions} transactions | Total WHT: {formatCurrency(reportData.summary?.total_wht_amount)}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-wht-csv">
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </Button>
                <TaxReturnExportButtons endpoint="wht-remittance" startDate={startDate} endDate={endDate} label="schedule" testIdPrefix="wht-schedule-export" />
                {selectedTransactions.length > 0 && (
                  <Button 
                    size="sm" 
                    onClick={() => setShowRemitDialog(true)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="mark-remitted-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Mark {selectedTransactions.length} as Remitted
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary" className="space-y-4">
              <TabsList>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="by_vendor">By Vendor</TabsTrigger>
                <TabsTrigger value="transactions">All Transactions</TabsTrigger>
              </TabsList>
              
              <TabsContent value="summary" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">Total Gross</p>
                      <p className="text-xl font-bold">{formatCurrency(reportData.summary?.total_gross_amount)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">Total WHT</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(reportData.summary?.total_wht_amount)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">{splitA}</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(reportData.summary?.corporate_wht)}</p>
                      <p className="text-xs text-muted-foreground">{reportData.by_entity_type?.corporate?.count || 0} transactions</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">{splitB}</p>
                      <p className="text-xl font-bold text-purple-600">{formatCurrency(reportData.summary?.individual_wht)}</p>
                      <p className="text-xs text-muted-foreground">{reportData.by_entity_type?.individual?.count || 0} transactions</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* By Category */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">By Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {reportData.by_category?.map((cat, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="font-medium">{cat.category}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">{cat.count} payments</span>
                            <span className="font-medium">{formatCurrency(cat.total_wht)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="by_vendor" className="space-y-4">
                <div className="space-y-2">
                  {reportData.by_vendor?.map((vendor, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {vendor.entity_type === 'corporate' ? (
                              <Building className="w-5 h-5 text-blue-500" />
                            ) : (
                              <User className="w-5 h-5 text-purple-500" />
                            )}
                            <div>
                              <p className="font-medium">{vendor.vendor_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {vendor.transaction_count} payments | Gross: {formatCurrency(vendor.total_gross)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{formatCurrency(vendor.total_wht)}</p>
                            <Badge variant={vendor.entity_type === 'corporate' ? 'default' : 'secondary'}>
                              {vendor.entity_type}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="transactions">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">
                    {selectedTransactions.length} of {reportData.transactions?.length || 0} selected
                  </p>
                  <Button variant="outline" size="sm" onClick={selectAllUnremitted}>
                    Select All Unremitted
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Vendor</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Category</th>
                        <th className="px-3 py-2 text-right">Gross</th>
                        <th className="px-3 py-2 text-center">Rate</th>
                        <th className="px-3 py-2 text-right">WHT</th>
                        <th className="px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.transactions?.map((t) => (
                        <tr key={t.id} className="border-b hover:bg-muted/50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedTransactions.includes(t.id)}
                              onChange={() => toggleTransaction(t.id)}
                              disabled={t.remitted}
                              className="rounded border-gray-300"
                              data-testid={`wht-tx-checkbox-${t.id}`}
                            />
                          </td>
                          <td className="px-3 py-2">{t.payment_date}</td>
                          <td className="px-3 py-2 font-medium">{t.vendor_name}</td>
                          <td className="px-3 py-2">
                            <Badge variant={t.entity_type === 'corporate' ? 'default' : 'secondary'} className="text-xs">
                              {t.entity_type === 'corporate' ? <Building className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                              {t.entity_type}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">{t.category}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(t.gross_amount)}</td>
                          <td className="px-3 py-2 text-center">{t.wht_rate}%</td>
                          <td className="px-3 py-2 text-right font-medium text-red-600">{formatCurrency(t.wht_amount)}</td>
                          <td className="px-3 py-2 text-center">
                            {t.remitted ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Remitted
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* WHT Rates Reference */}
      {whtConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{taxName} rates — {whtConfig.country} statutory pack</CardTitle>
          </CardHeader>
          <CardContent>
            {whtConfig.rates?.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="wht-rates-grid">
                {whtConfig.rates.map((rate, idx) => (
                  <div key={idx} className="p-3 bg-muted rounded-lg">
                    <p className="font-medium text-sm">{rate.category}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      {rate.corporate_rate === rate.individual_rate ? (
                        <span className="text-slate-700">{rate.corporate_rate}%</span>
                      ) : (
                        <>
                          <span className="text-blue-600">{splitA}: {rate.corporate_rate}%</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-purple-600">{splitB}: {rate.individual_rate}%</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="wht-rates-empty">
                {profile.regime === 'none' ? `No statutory withholding rates apply to ordinary supplier payments in ${whtConfig.country}.` : `No rate table in the ${whtConfig.country} tax pack yet.`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Remittance Dialog */}
      <Dialog open={showRemitDialog} onOpenChange={setShowRemitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark WHT as Remitted</DialogTitle>
            <DialogDescription>
              Record the remittance of {selectedTransactions.length} WHT transactions to the tax authority.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Remittance Reference *</Label>
              <Input
                placeholder={`e.g., WHT-${new Date().getFullYear()}-001`}
                value={remittanceRef}
                onChange={(e) => setRemittanceRef(e.target.value)}
                data-testid="remittance-ref-input"
              />
              <p className="text-xs text-muted-foreground">
                The reference number from the tax authority payment confirmation
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Remittance Date</Label>
              <Input
                type="date"
                value={remittanceDate}
                onChange={(e) => setRemittanceDate(e.target.value)}
                data-testid="remittance-date-input"
              />
            </div>
            
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">Selected Transactions: {selectedTransactions.length}</p>
              <p className="text-sm text-muted-foreground">
                Total WHT: {formatCurrency(
                  reportData?.transactions
                    ?.filter(t => selectedTransactions.includes(t.id))
                    ?.reduce((sum, t) => sum + t.wht_amount, 0) || 0
                )}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemitDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleMarkRemitted} 
              disabled={remitting || !remittanceRef.trim()}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-remittance-btn"
            >
              {remitting ? 'Processing...' : 'Confirm Remittance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default WHTRemittanceReport;
